
import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

// Types
export type MailTemplate = {
  _id: string;
  name: string;
  subject: string;
  htmlContent: string;
  file_url?: string | null;
  variables: string[];
  classes: Array<string | { _id: string; name?: string; classIdentifier?: string }>;
  category: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
};

export type EmailPlaceholder = {
  label: string;
  value: string;
};

export type EmailType = {
  id: string;
  label: string;
};

export type MailTemplateFormData = {
  name: string;
  subject: string;
  htmlContent: string;
  category: string;
  variables: string[];
  classes: string[];
  isActive: boolean;
  file?: File | null;
};

export type MailTemplateUpdateData = Partial<MailTemplateFormData>;

export type PaginationData = {
  total: number;
  totalPages: number;
  currentPage: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

export type MailTemplatesResponse = {
  success: boolean;
  data: MailTemplate[];
  pagination: PaginationData;
};

export type MailTemplateResponse = {
  success: boolean;
  data: MailTemplate;
};

export type PlaceholdersResponse = {
  success: boolean;
  data: EmailPlaceholder[];
};

export type EmailTypesResponse = {
  success: boolean;
  data: EmailType[];
};

export type ApiResponse<T = any> = {
  success: boolean;
  data: T;
  message?: string;
};

export type SendTestTemplateEmailRequest = {
  to: string;
  cc?: string | null;
  bcc?: string | null;
  /**
   * Can be { key: value } or [{ key, value }] (backend supports both)
   */
  variables?: Record<string, any> | Array<{ key: string; value: any }>;
};

export type GetAllTemplatesParams = {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  searchText?: string;
  category?: string;
  isActive?: string;
  classId?: string;
};

// Endpoints
const MAIL_TEMPLATE_ENDPOINT = {
  MAIL_TEMPLATES: "/mailTemplate",
  PLACEHOLDERS: "/mailTemplate/placeholder",
  EMAIL_TYPES: "/mailTemplate/emailTypes",
};

// API Functions

/**
 * Get all mail templates with pagination and filters
 */
 const getAllMailTemplates = async (
  params: GetAllTemplatesParams = {}
): Promise<MailTemplatesResponse> => {
  const {
    page = 1,
    limit = 10,
    sortBy = "createdAt",
    sortOrder = "desc",
    searchText = "",
    category = "",
    isActive = "",
    classId = "",
  } = params;

  const response = await apiClient({
    url: appendQueryParams(MAIL_TEMPLATE_ENDPOINT.MAIL_TEMPLATES, {
      page,
      limit,
      sortBy,
      sortOrder,
      searchText,
      category,
      isActive,
      classId,
    }),
    method: "GET",
  });
  return response.data;
};

/**
 * Get single mail template by ID
 */
 const getMailTemplateById = async (
  id: string
): Promise<MailTemplateResponse> => {
  const response = await apiClient({
    url: `${MAIL_TEMPLATE_ENDPOINT.MAIL_TEMPLATES}/${id}`,
    method: "GET",
  });
  return response.data;
};

/**
 * Create new mail template with file upload
 */
 const createMailTemplate = async (
  data: MailTemplateFormData
): Promise<MailTemplateResponse> => {
  const formData = new FormData();
  
  // Append all fields to FormData
  formData.append("name", data.name);
  formData.append("subject", data.subject);
  formData.append("htmlContent", data.htmlContent);
  formData.append("category", data.category);
  formData.append("isActive", data.isActive.toString());
  
  if (data.variables && data.variables.length > 0) {
    formData.append("variables", JSON.stringify(data.variables));
  }
  if (data.classes?.length) {
    formData.append("classes", JSON.stringify(data.classes));
  }
  if (data.file) {
    formData.append("file", data.file);
  }

  const response = await apiClient({
    url: MAIL_TEMPLATE_ENDPOINT.MAIL_TEMPLATES,
    method: "POST",
    data: formData,
  });
  return response.data;
};

/**
 * Update existing mail template
 */
 const updateMailTemplate = async (
  id: string,
  data: MailTemplateUpdateData
): Promise<MailTemplateResponse> => {
  const response = await apiClient({
    url: `${MAIL_TEMPLATE_ENDPOINT.MAIL_TEMPLATES}/${id}`,
    method: "PUT",
    data,
  });
  return response.data;
};

/**
 * Delete mail template (soft delete)
 */
 const deleteMailTemplate = async (
  id: string
): Promise<ApiResponse<{ message: string }>> => {
  const response = await apiClient({
    url: `${MAIL_TEMPLATE_ENDPOINT.MAIL_TEMPLATES}/${id}`,
    method: "DELETE",
  });
  return response.data;
};

/**
 * Get email placeholders/variables
 */
 const getEmailPlaceholders = async (): Promise<PlaceholdersResponse> => {
  const response = await apiClient({
    url: MAIL_TEMPLATE_ENDPOINT.PLACEHOLDERS,
    method: "GET",
  });
  return response.data;
};

/**
 * Get available email types/categories
 */
 const getEmailTypes = async (): Promise<EmailTypesResponse> => {
  const response = await apiClient({
    url: MAIL_TEMPLATE_ENDPOINT.EMAIL_TYPES,
    method: "GET",
  });
  return response.data;
};

/**
 * Send a test email for an existing template ID
 * Backend expects: POST /mailTemplate/:id/test
 */
const sendTestTemplateEmail = async (
  id: string,
  data: SendTestTemplateEmailRequest
): Promise<ApiResponse<any>> => {
  const response = await apiClient({
    url: `${MAIL_TEMPLATE_ENDPOINT.MAIL_TEMPLATES}/${id}/test`,
    method: "POST",
    data,
  });
  return response.data;
};

/**
 * Get file download URL (presigned URL)
 */
 const downloadTemplateFile = async (fileUrl: string): Promise<string> => {
  const response = await apiClient({
    url: `/download?fileUrl=${encodeURIComponent(fileUrl)}`,
    method: "GET",
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  return url;
};

/**
 * Download file and trigger browser download
 */
 const downloadFile = async (fileUrl: string, fileName: string): Promise<void> => {
  try {
    const response = await apiClient({
      url: `/download?fileUrl=${encodeURIComponent(fileUrl)}`,
      method: "GET",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

/**
 * Validate template form data
 */
 const validateTemplateForm = (data: MailTemplateFormData): string[] => {
  const errors: string[] = [];

  if (!data.name?.trim()) {
    errors.push("Template name is required");
  }

  if (!data.subject?.trim()) {
    errors.push("Email subject is required");
  }

  if (!data.htmlContent?.trim()) {
    errors.push("Email body is required");
  }

  if (!data.category?.trim()) {
    errors.push("Email type is required");
  }

  if (data.file && data.file.size > 10 * 1024 * 1024) {
    errors.push("File size should not exceed 10MB");
  }

  return errors;
};

/**
 * Format template data for form
 */
 const formatTemplateForForm = (
  template: MailTemplate
): MailTemplateFormData => {
  return {
    name: template.name,
    subject: template.subject,
    htmlContent: template.htmlContent,
    category: template.category,
    variables: template.variables || [],
    classes: template.classes?.map((c) =>
      typeof c === "object" && "_id" in c ? c._id : (c as string)
    ) || [],
    isActive: template.isActive,
    file: null,
  };
};

 const getClassNames = (template: MailTemplate): string => {
  if (!template.classes?.length) return "No classes assigned";
  return template.classes
    .map((c) => (typeof c === "object" && c.name ? c.name : "Unknown"))
    .join(", ");
};
export {
  getAllMailTemplates,
  getMailTemplateById,
  createMailTemplate,
  updateMailTemplate,
  deleteMailTemplate,
  getEmailPlaceholders,
  getEmailTypes,
  sendTestTemplateEmail,
  downloadTemplateFile,
  downloadFile,
  validateTemplateForm,
  formatTemplateForForm,
  getClassNames
};